import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const themeListeners = new Set<() => void>();
let themeVersion = 0;

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

function publish(): void {
  themeVersion += 1;
  for (const listener of themeListeners) listener();
}

export function getThemePreference(): ThemePreference {
  const stored = document.documentElement.dataset.themePreference;
  return stored === 'light' || stored === 'dark' ? stored : 'system';
}

export function getResolvedTheme(): ResolvedTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function setThemePreference(preference: ThemePreference): void {
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.theme = resolve(preference);
  document.cookie = `varza.theme=${preference}; Path=/; Max-Age=31536000; SameSite=Lax`;
  publish();
}

function subscribe(listener: () => void): () => void {
  themeListeners.add(listener);
  if (themeListeners.size === 1 && typeof window.matchMedia === 'function') {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', onSystemThemeChange);
  }
  return () => {
    themeListeners.delete(listener);
    if (themeListeners.size === 0 && typeof window.matchMedia === 'function') {
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', onSystemThemeChange);
    }
  };
}

function onSystemThemeChange(): void {
  if (getThemePreference() !== 'system') return;
  document.documentElement.dataset.theme = resolve('system');
  publish();
}

export function useTheme() {
  useSyncExternalStore(subscribe, () => themeVersion, () => themeVersion);
  return {
    preference: getThemePreference(),
    theme: getResolvedTheme(),
    setPreference: setThemePreference,
    toggle: () => setThemePreference(getResolvedTheme() === 'dark' ? 'light' : 'dark'),
  };
}
